import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DateFormat from '../components/DateFormat';
import { useUserData } from '../context/UserData';
import { useNavigate, NavLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import HeadingCp from '../components/HeadingCp';
import MonthTabs from '../components/MonthTabs';
import Truncate from '../components/Truncate';
import { CheckBadgeIcon, DocumentCheckIcon, FlagIcon } from '@heroicons/react/24/outline';
import SubMenu from '../components/SubMenu';
import LegendCP from '../components/LegendCP';
import SearchBarCP from '../components/SearchBarCP';
import ModalWindow from '../components/ModalWindow';
import { PhotoIcon } from '@heroicons/react/24/solid';
import Badge from '../components/Badge';
import FileLocation from '../components/FileLocation';
import WipFormModal from '../components/WipFormModal';

const WipScheduleListView = () => {
  const [technicians, setTechnicians] = useState([]);  
  const [followupId, setFollowupId] = useState(null);
  const [notesIdOpen, setNotesIdOpen] = useState(null);
  const [inspectionNotesIdOpen, setInspectionNotesIdOpen] = useState(null);
  const [scheduleJob, setScheduleJob] = useState([]);
  const [filterInspection, setFilterInspection] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noAccess, setNoAccess] = useState(true);

 // Confirm Delete Modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);  

  const [isEditing, setIsEditing] = useState(false);  
  const [disabled, setDisabled] = useState(false);  

  const [searchTerm, setSearchTerm] = useState('');
  const [billingFilter, setBillingFilter] = useState('all'); // new state for billing filter

  const userData = useUserData();
  const navigate = useNavigate();

  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = new Date();
    return String(date.getMonth() + 1).padStart(2, '0');
  });

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    job_number: '',
    tech_id: '',
    schedule_from: '',
    schedule_to: '',
  });  

  const apiUrl = process.env.REACT_APP_API_URL;
  const api = `${apiUrl}/wip_crud.php`;
  const appRoot = process.env.REACT_APP_ROOT_URL;

  var expFileUrl = `${appRoot}maintenance_files/`;

  // State for Google Drive images
  const [imagesModalOpen, setImagesModalOpen] = useState(false);
  const [driveImages, setDriveImages] = useState([]);

  //file upload state
  const [file, setFile] = useState(null);
  const [selectGenFile, setSelectGenFile] = useState(null);
  const [isModalFileGenOpen, setIsModalFileGenOpen] = useState(false);

  // Define your month tabs
  const months = [
    { name: 'All', value: 'all' },
    { name: 'January', value: '01' },
    { name: 'February', value: '02' },
    { name: 'March', value: '03' },
    { name: 'April', value: '04' },
    { name: 'May', value: '05' },
    { name: 'June', value: '06' },
    { name: 'July', value: '07' },
    { name: 'August', value: '08' },
    { name: 'September', value: '09' },
    { name: 'October', value: '10' },
    { name: 'November', value: '11' },
    { name: 'December', value: '12' },
  ];

  // Fetch scheduleJob on initial load or when month changes
  useEffect(() => {
    fetchScheduleList(noAccess);
    fetchTechnicians();
  }, [noAccess]);

  // Whenever scheduleJob, searchTerm, or billingFilter changes, apply filters
  useEffect(() => {
    applyFilters(scheduleJob, searchTerm, billingFilter);
  }, [scheduleJob, searchTerm, billingFilter]);

  // Combine search & billing filters
  const applyFilters = (allInspections, search, billing) => {
    let filtered = [...allInspections];

    // 1) Search filter
    if (search) {
      filtered = filtered.filter((insp) =>
        (insp.job_site && insp.job_site.toLowerCase().includes(search.toLowerCase())) ||
        (insp.customer_name && insp.customer_name.toLowerCase().includes(search.toLowerCase())) ||
        (insp.type && insp.type.toLowerCase().includes(search.toLowerCase()))
      );
    }

    // 2) Billing filter
    //  Make sure to convert billing_complete to number if it’s stored as a string
    if (billing === 'invoiced') {
      filtered = filtered.filter((insp) => insp.status === 'Completed' && Number(insp.invoiced) === 1);
    } else if (billing === 'notInvoiced') {
      filtered = filtered.filter((insp) => insp.status === 'Completed' &&( Number(insp.invoiced) === 0 || insp.invoiced === null));
    } else if (billing === 'complete') {
      filtered = filtered.filter((insp) => insp.status === 'Completed');
    } else if (billing === 'pending') {
      filtered = filtered.filter((insp) => insp.status === 'Active' || insp.status === null || insp.status === '');
    } else if (billing === 'ready') {
      filtered = filtered.filter((insp) => insp.status === 'Completed' && Number(insp.billable) === 1);
    }    

    setFilterInspection(filtered);
  };

  const fetchScheduleList = async (selectedMonth = currentMonth) => {
    setLoading(true);
    try {
      const response = await axios.get(api, {
        params: {
          getWipScheduleListView: 'all',
          month: selectedMonth,
        },
      });
      if (response.data[0]) {
        setScheduleJob(response.data[0] || []);
        if(response.data[1]){
          setNoAccess(false);
        }
      }

    } catch (error) {
      // Handle errors
      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error('Access denied.', {
          position: 'top-center',
        });
      } else {
        toast.error('Error fetching scheduleJob', {
          position: 'top-center',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (monthValue) => {
    setCurrentMonth(monthValue);
    fetchScheduleList(monthValue);
  };

  // Toggle billing_complete between 1 and 0
  const handleBillingComplete = async (schedule_id, billingComplete) => {
    billingComplete = billingComplete === 1 ? 0 : 1;

    try {
      const response = await axios.post(
        api,
        {
          status: 'billingComplete',
          data: {
            schedule_id: schedule_id,
            billable: billingComplete,
          },
        },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );
      if (response.data[0] === 'success') {
        toast.success('updated!', { position: 'top-center' });
        fetchScheduleList();
      } else {
        toast.error(response.data.error || 'Error updating', { position: 'top-center' });
      }
    } catch (error) {
      console.error('Error updating billing', error);
      toast.error('Error updating billing', { position: 'top-center' });
    }
  };

  const handleInvoiced = async (schedule_id, invoicedCompleted, status) => {
    if (status === 'pending') {
      toast.info('Schedule is not completed', { position: 'top-center' });
      return;
    }
    invoicedCompleted = invoicedCompleted === 1 ? 0 : 1;

    try {
      const response = await axios.post(
        api,
        {
          status: 'invoicedCompleted',
          data: {
            schedule_id: schedule_id,
            invoiced: invoicedCompleted,
          },
        },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );
      if (response.data[0] === 'success') {
        toast.success('updated!', { position: 'top-center' });
        fetchScheduleList();
      } else {
        toast.error(response.data.error || 'Error updating', { position: 'top-center' });
      }
    } catch (error) {
      console.error('Error updating billing', error);
      toast.error('Error updating billing', { position: 'top-center' });
    }
  };

  //Handler to open the file upload modal
  const handleGenUploadFile = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a file before uploading.", { position: 'top-center' });
      return;
    }
    
    const formData = new FormData();
    formData.append('status', 'submit_gen_file');
    formData.append('id', selectGenFile); // Contract ID
    formData.append('file', file);
    try {
      const response = await axios.post(`${apiUrl}/inspection_file_upload.php`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        toast.info('File uploaded successfully!', { position: 'top-center' });

        // Update the state with the new file information
        setFilterInspection(prevItems =>
        prevItems.map(item => {
          return item.id === selectGenFile ? { ...item, invoiced_file:{...item.invoiced_file, invoiced_file: response.data[0]}}: item
        } ));

        setFile(null);
        setIsModalFileGenOpen(false);
      } else {
        toast.error(response.data.error || "Error uploading file", { position: 'top-center' });
      }
    } catch (error) {
      toast.error("An error occurred during file upload.", { position: 'top-center' });
      //console.error('Error uploading file', error);
    }
  };  

  //handle file change
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      toast.error("No file selected.", { position: 'top-center' });
      setFile(null);
      return;
    }

    const validFileTypes = ['application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 2MB in bytes

    if (!validFileTypes.includes(selectedFile.type)) {
      toast.error("Invalid file type. Only PDF is allowed.", { position: 'top-center' });
      setFile(null);
      return;
    }

    if (selectedFile.size > maxSize) {
      toast.error("File size exceeds 2MB.", { position: 'top-center' });
      setFile(null);
      return;
    }

    setFile(selectedFile);
  };  

  const voidContractSubmit = (contractId) => {
    // ...
  };

  // Update local state whenever user types in the search input
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // to override the local filtering. Could also rely 100% on local filter if your dataset is small.
  const handleSearchClick = async () => {
    setLoading(true);
    const bodyData = {
      status: 'searchInspections',
      data: { search: searchTerm },
    };
  
    try {
      const response = await axios.post(api, bodyData, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.data && Array.isArray(response.data)) {
        setScheduleJob(response.data);
        setSearchTerm(''); // <---- clear searchTerm so local filter is not re-applied
      } else {
        setFilterInspection([]);
        toast.error('No results found for: ' + searchTerm, { position: 'top-center' });
      }
    } catch (error) {
      console.error('Error searching contracts', error);
      toast.error('Error searching contracts', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  const handleNotesView = (id) => {
    setNotesIdOpen(id);
  };

  const handleInspectionNotesView = (id) => {
    setInspectionNotesIdOpen(id);
  };

  const handleViewImages = async (Schedule) => {
      // 1) Check if we have an array of folder references
    if (!Schedule.inspection_images || !Schedule.inspection_images.length) {
      toast.error('No folders for this inspection');
      return;
    }

    try {
      let allFiles = [];
      
      // 2) Loop over each item in inspection_images
      for (const folderRow of Schedule.inspection_images) {
        const folderId = folderRow.image_name;  // the actual Drive folder ID

        // 3) Call the backend to list files in that folder
        const response = await axios.get(`${apiUrl}/drive_folder.php`, {
          params: {
            status: 'listFiles',
            folderId: folderId
          }
        });

        if (response.data.success && Array.isArray(response.data.files)) {
          // Merge these files into allFiles
          allFiles = [...allFiles, ...response.data.files];
        } else {
          console.warn(`No success for folderId=${folderId}`, response.data);
        }
      }

      // 4) Once all calls are done, store combined files in driveImages
      setDriveImages(allFiles);
      setImagesModalOpen(true);
    } catch (error) {
      console.error('Error listing images across multiple folders:', error);
      toast.error('Error listing images', { position: 'top-center' });
    }
  };

  const getLinksForJob = (object) => {
    const links = [
      { name: 'Edit Schedule', click: () => openModal(object, true), key: `edit-${object.id}` },
      // { name: 'Void Call', click: () => handleVoidServiceCall(object.id), key: `void-${object.id}` },
    ];
    if (object.status === "Completed") {
      if(object.invoiced === 0 || object.invoiced === null){ 
      links.push({
        name: object?.billable ? 'Remove Billable' : 'Billable',
        click: () =>
          handleBillingComplete(
            object.schedule_id,
            object.billable ? 1 : 0,
          ),
      });
      }
    }  
    if (object.billable === 1 && noAccess === false) {
      links.push({
        name: object?.invoiced === 1 ? 'Not Invoiced' : 'Invoiced',
        click: () => handleInvoiced(object.schedule_id, object.invoiced ? 1 : 0, object.status),
      });      
    }

    return links
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleApiError = (error) => {
    if (error.response && error.response.status === 401) {
      toast.error('Session expired. Please login again.', {
        position: 'top-center',
      });
      navigate('/login');
    } else if (error.response && error.response.status === 403) {
      toast.error('Access denied.', {
        position: 'top-center',
      });
    } else {
      toast.error('An error occurred while fetching data.', {
        position: 'top-center',
      });
    }
  };  

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if(formData.end_time < formData.start_time)
    {
      toast.error('Schedule time out must be later than schedule time in.', {
        position: 'top-center',
      });
      return;
    }

    if(!formData.job_number || !formData.tech_id || !formData.schedule_from || !formData.schedule_to) {
      toast.error('Please fill in all required fields. (start/end date)', {
        position: 'top-center',
      });
      return;
    }

    try {
      const response = await axios.post(`${apiUrl}/wip_crud.php`, {
        data: formData,
        status: 'recordJob',
      });
      if (response.data.success) {
        toast.success('Inspection saved successfully!');
        // 1) Reset the form data, including job site fields
        setFormData({
          id: '',
          job_number: '',
          schedule_from: '',
          schedule_to: '',
          date: '',
          status: '',
          tech_id: '',
          start_time: '',
          end_tme: '',
          user_comment: '',
          billable: '',
          invoiced:'',
          flagged: '',
          additional_tech_id: '',
          additional_tech_name: ''
        });
        // 2) Close the modal and reload your data
        closeModal();
        fetchScheduleList(currentMonth)
      } else {
        toast.error(response.data.error || 'Failed to save inspection.');
      }
    } catch (error) {
      handleApiError(error);
    }
  };  

  const handleDeleteRequest = () => {
    setDeleteId(formData?.schedule_id);
    setShowConfirmModal(true);
    setIsModalOpen(false);
  };

  const openModal = (insp, isEdit = false) => {
    setIsModalOpen(true);
    setIsEditing(isEdit);
  
    if (!isEdit) {
      setSelectedInspection(null);
      setFormData({
        schedule_id: '',
        job_number: '',
        tech_id: '',
        schedule_from: '',
        schedule_to: '',
        date: '',
        status: '',
      });
    } else {
      setSelectedInspection(insp);
      setFormData({
        ...insp,
        id: insp.schedule_id,
      });
    }
  };
  

  const handleEditInspection = () => {
    const editableInspection = {
      ...selectedInspection,
      id: selectedInspection.schedule_id, // rename for the form
    };
    setFormData(editableInspection);
    setIsDetailModalOpen(false);
    setIsModalOpen(true);
    setIsEditing(true);
  };  

  const fetchTechnicians = async () => {
    try {
      const response = await axios.get(`${apiUrl}/contracts_crud.php`, {
        params: { employee_list: 'true' },
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      });
      const employeesData = Array.isArray(response.data) ? response.data : [];      
      if (employeesData.length === 0) {
        toast.error('No maintenance technicians found!', { position: 'top-center' });
      }
   /* ----------------------------------------------------------
    SORT technicians:
    1) by department  (A->Z)
    2) inside each department by last name  (A->Z)
    ---------------------------------------------------------- */
   const sorted = [...employeesData].sort((a, b) => {
   const depA = (a.department  || '').toLowerCase();
   const depB = (b.department  || '').toLowerCase();
   if (depA !== depB) return depA.localeCompare(depB);
   const lastA = (a.last_name   || '').toLowerCase();
   const lastB = (b.last_name   || '').toLowerCase();
    return lastA.localeCompare(lastB);
  });
  setTechnicians(sorted);                 
      // setTechnicians(employeesData);
    } catch (error) {
      handleApiError(error);
    }
  };  


  return (
    <div className="mx-auto max-w-[1250px] px-4 sm:px-6 lg:px-8 py-8">
      <HeadingCp label="Wip Report List" />

      {/* Month Tabs */}
      <MonthTabs months={months} currentMonth={currentMonth} onMonthChange={handleMonthChange} />

      <br />
      {/* <LegendCP color="fill-green-300" label="Ready For Billing" /> */}
      <br />
      <br />

      <div className="flex space-x-3 items-center mb-10">
      <NavLink to="/full_scheduler_viewer" className="hidden sm:inline-flex">
        <button
          type="button"
          className="w-full sm:w-auto px-4 py-2 border border-transparent text-sm font-medium 
                    rounded-md shadow-sm text-white bg-yellow-400 hover:bg-yellow-700 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
        >
          Full Scheduler
        </button>
      </NavLink>          
      <button
          type="button"
          onClick={() => openModal(false)}
          className="px-4 py-2 border border-transparent text-sm font-medium rounded-md 
            shadow-sm text-white bg-orange-400 hover:bg-orange-700 focus:outline-none 
            focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
        >
          New Schedule +
        </button>

        <button
          onClick={() => navigate(`/wip_scheduler`)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium 
                    rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-400 focus:outline-none 
                    focus:ring-2 focus:ring-offset-2 focus:ring-black-500"
        >
          Wip Calendar
        </button>      
 
        {/* New billing filter dropdown */}
        <select
          className="border px-2 py-1 rounded"
          value={billingFilter}
          onChange={(e) => setBillingFilter(e.target.value)}
        >
          <option value="all">All</option>
          <option value="invoiced">Invoiced</option>
          <option value="notInvoiced">Not Invoiced</option>
          <option value="pending">Pending</option>
          <option value="complete">Complete</option>
          <option value="ready">Ready Not Invoiced</option>
        </select>

        {/* Search Bar */}
        <SearchBarCP search={searchTerm} handleChange={handleSearchChange} onClick={handleSearchClick} />
      </div>

      {loading ? (
      <LoadingSpinner>Loading...</LoadingSpinner>
      ) : (
        <>
        <table className="min-w-full divide-y divide-gray-300 bg-gray-200 border rounded-lg">
          <thead>
            <tr className="divide-x divide-gray-400">
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">ID|J#</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Tech | Job site | Customer
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Job Description</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Report /Img</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Scheduled Date</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
               Logged
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Notes</th>
            </tr>
          </thead>
          <tbody>

          {filterInspection?.map((Schedule, index) => {
            // Highlight row if billing_status === 'ready'
            const rowClassName = () => {
              if (Schedule.billable === 1) {
                return 'bg-green-100 divide-x';
              } else {
                return `${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'} divide-x`;
              }
            };

            return (
              <tr key={Schedule.schedule_id} className={rowClassName()}>
                <td className="px-3 py-4 text-sm text-gray-500">
                  <div className="text-sm text-gray-400">
                    <SubMenu label={Schedule.schedule_id} idItem={Schedule.schedule_id} links={getLinksForJob(Schedule)} />
                    {Schedule.invoiced === 1 && (
                      <span>
                        <CheckBadgeIcon className="w-5 text-blue-700 font-extrabold" />
                      </span>
                    )}     
                    <div>
                      {Schedule.job_number}
                    </div>
                  </div>
                </td>

                {/* tech | Schedule site | customer  */}
                <td className="px-3 py-4 text-sm text-gray-500">
                  <div className="text-green-700">
                    by: {Schedule.tech_name}
                  </div>
                  <span className="font-bold text-black">
                    {Schedule.job_site}
                  </span>
                  <p className="text-sm text-gray-400">
                    {Schedule.customer_name}
                  </p>
                </td>

                <td className="px-3 py-4 text-sm text-gray-500">
                  {Schedule.job_description}
                  {/* {Schedule.contact_name}
                  <p>{Schedule.contact_phone && ' ' + Schedule.contact_phone + ', '}</p>
                  <p>{Schedule.contact_email && ' ' + Schedule.contact_email + ' '}</p> */}
                </td>

                {/* file */}
                <td className="px-3 py-4 text-sm text-gray-500">
                  <span className="text-gray-700 p-1 flex items-center hover:cursor-pointer">
                    {Schedule.status === 'Completed' && (
                      <NavLink
                        to={`/wip_report/${Schedule.schedule_id}`}
                        target="_blank"
                        className={"flex items-center"}
                        rel="noopener noreferrer"
                      >
                        <span><DocumentCheckIcon className="w-5 text-green-600 font-bold" /></span>
                        <span className='text-green-600 '>Report</span>
                      </NavLink>
                    )}
                  </span>
                  <span>
                    {/* if you store multiple images in Google Drive, show a “View Images” button */}
                    {Schedule.inspection_images && (
                      <button
                        onClick={() => handleViewImages(Schedule)}
                        className="flex items-center gap-1 text-blue-700 hover:underline"
                      >
                        <PhotoIcon className="w-5 text-blue-600 font-bold" />
                        Images
                      </button>
                    )}
                  </span>
                </td>
                
                {/* date */}
                <td className="px-3 py-4 text-xs text-gray-500">
                  <DateFormat date={Schedule.schedule_from} /> /<br />
                  <DateFormat date={Schedule.schedule_to} />
                  <p className="text-xs text-gray-400">
                    {Schedule.start_time} - {Schedule.end_time}
                  </p>
                </td>

                {/* logged */}
                <td className="px-3 py-4 text-sm text-gray-500">
                  <DateFormat date={Schedule.date} />
                  <p className="text-xs text-gray-400">
                    {Schedule.user}
                  </p>
                </td>

                {/* status */}
                <td className="px-3 py-4 text-sm text-gray-500">
                  {Schedule.status === 'Completed' ? <Badge color={'green'} feed={Schedule.status}/> : 
                                                     <Badge color={'indigo'} feed={Schedule.status}/> 
                  }
                  {Schedule.flagged === 1 && (
                    <span className="flex items-center mb-1 text-red-700 mt-1">
                      <FlagIcon className="w-5" /> Flagged
                    </span>
                  )}
                </td>

                <td className="px-3 py-4 text-sm text-gray-500">
                  {Schedule.tech_notes && (
                    <span
                      className="flex items-center mb-1 hover:cursor-pointer text-indigo-700"
                      onClick={() => setNotesIdOpen(Schedule.schedule_id)}
                    >
                      <DocumentCheckIcon className="w-5" /> Tech
                    </span>
                  )}

                  {notesIdOpen === Schedule.schedule_id && (
                    <ModalWindow
                      job={Schedule.tech_notes}
                      closeNotes={() => setNotesIdOpen(null)}
                      activeButton={true}
                    />
                  )}

                  {Schedule.user_comment && (
                    <span
                      className="flex items-center mb-1 hover:cursor-pointer text-purple-800 mt-2"
                      onClick={() => setInspectionNotesIdOpen(Schedule.schedule_id)}
                    >
                      <DocumentCheckIcon className="w-5" /> PM Notes
                    </span>
                  )}

                  {inspectionNotesIdOpen === Schedule.schedule_id && (
                    <ModalWindow
                      job={Schedule.user_comment}
                      closeNotes={() => setInspectionNotesIdOpen(null)}
                      activeButton={true}
                    />
                  )}
                </td>
              </tr>
            );
          })}
          </tbody>
        </table>   

        {imagesModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded p-4 max-w-3xl w-full">
              <h2 className="text-lg font-bold mb-3">Images for Inspection</h2>
              <button
                className="text-red-500 float-right"
                onClick={() => setImagesModalOpen(false)}
              >
                Close
              </button>
              <br />
              <div className="grid grid-cols-3 gap-2 mt-8">
                {driveImages?.map((file) => (
                  <div key={file.id} className="border p-2">
                    <p className="text-sm font-medium">{file.name}</p>
                    {/* If it's an image, show a preview */}
                    {/* https://drive.google.com/uc?export=view&id=ACTUAL_FILE_ID */}
                    {file?.id && file?.mimeType.startsWith('image/') ? (
                      <>            
                      <NavLink to={file?.webViewLink} target="_blank">
                      <img
                        // src={`https://drive.google.com/uc?export=view&id=${file?.id}`}
                        src={`https://drive.google.com/thumbnail?id=${file?.id}&sz=w1000`}
                        // src={`https://drive.google.com/uc?export=view&id=${file?.id}`}
                        alt="view file"
                        className="mt-2 w-full h-auto object-cover">          
                        </img>
                        </NavLink>         
                      </>
                    ) : (
                      <a
                        href={file.webViewLink}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-500 underline"
                      >
                        Open file
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {followupId && (
          <ModalWindow
            job={
              <ul>
                {followupId.map((issue) => (
                  <li key={issue.id} className="text-sm text-gray-500">
                    {/* Example fields to display: */}
                    {issue.device} – {issue.device_location} – {issue.issue}
                  </li>
                ))}
              </ul>
            }
            closeNotes={() => setFollowupId(null)}
            activeButton={true}
          />
        )}

        {/* file upload */}
        {isModalFileGenOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50">
            <div className="bg-white p-8 rounded-md shadow-md min-w-[500px]">
              <h2 className="text-xl font-semibold mb-4">Upload Invoice</h2>
              <form onSubmit={handleGenUploadFile}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">Select a PDF file (Max size 2MB)</label>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileChange}
                    className="mt-2"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setIsModalFileGenOpen(false)}
                    className="px-4 py-2 mr-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-700"
                  >
                    Upload
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}             

      {/* ---------------- WIP Form Modal ---------------- */}
      <WipFormModal
        isOpen={isModalOpen}
        onClose={closeModal}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleFormSubmit}
        technicians={technicians}
        handleDelete={handleDeleteRequest} 
        isEditing={isEditing}
        disabled={disabled}
      />        
        </>
      )}
    </div>
  );
};

export default WipScheduleListView;